/**
 * What an Entry's description says about spells (docs/adr/0003). Foundry's data has no Rule
 * Element for "you gain this focus spell" or "you can cast this cantrip as an innate spell": the
 * text is the only carrier, and Foundry's own sheet leaves adding the spell to the user. These
 * parsers read the regular forms the Packs use: labelled spell lists ("Bloodline Spells initial:
 * X, advanced: Y", a school's "Curriculum" bullets, "Order Spell X"), innate-spell sentences and
 * "you gain / learn the X spell" sentences. Everything they return is a suggestion the engine
 * checks against the catalogue (a linked spell must exist and, for a focus grant, carry the
 * focus trait); anything they miss the user adds by hand.
 */
import type { MagicTradition } from "../character/model";

export interface SpellLink {
  uuid: string;
  name: string;
}

/** A spell list under a bold label, split by the rank or role words in front of each group of links. */
export interface LabelledSpellList {
  /** The label text ("Bloodline Spells", "Curriculum", "Order Spell"). */
  label: string;
  groups: { role: SpellRole | null; spells: SpellLink[] }[];
}

/** "cantrip", a rank 1 to 10, or a focus-spell tier. */
export type SpellRole = "cantrip" | "initial" | "advanced" | "greater" | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/** "You can cast X as a primal innate spell at will" or "Choose one cantrip from the arcane spell list ... innate". */
export interface InnateStatement {
  spells: SpellLink[];
  tradition: MagicTradition | null;
  /** Uses per day; null for at will. */
  uses: number | null;
  /** The rank the spell is cast at, when the text names one. */
  rank: number | null;
  /**
   * The pick the text asks for, when it is the user's: a filter ("choose one cantrip from the
   * primal spell list") or named candidates ("Choose either Bless or Bane. You can cast the
   * chosen spell once per day as a 1st-rank divine innate spell.").
   */
  choose: { cantrip: boolean; rank: number | null; tradition: MagicTradition | null; candidates: SpellLink[] | null } | null;
}

/** A sentence that gives spells ("You gain X", "You learn X", "your familiar learns X", "Choose either X or Y"). */
export interface GrantSentence {
  spells: SpellLink[];
  /** The user picks one of `spells` (an "or" between the links, or "choose"). */
  choose: boolean;
  /** "... or another 1st-rank monk qi spell": any other spell that fits may be picked too. */
  another: { rank: number | null; trait: string | null } | null;
  /** "your familiar learns X": the spell joins the spellbook, not the focus list. */
  familiar: boolean;
  /** "You add X to your apparition spell repertoire": the repertoire the sentence names, when it names one. */
  repertoire: string | null;
  text: string;
}

const SPELL_LINK = /@UUID\[(Compendium\.pf2e\.spells-srd\.Item\.[^\]]+)\](?:\{([^}]*)\})?/g;
const TRADITIONS: readonly MagicTradition[] = ["arcane", "divine", "occult", "primal"];
const ORDINAL = /(\d+)(?:st|nd|rd|th)/;

function links(text: string): SpellLink[] {
  const out: SpellLink[] = [];
  for (const m of text.matchAll(SPELL_LINK)) out.push({ uuid: m[1]!, name: m[2] ?? m[1]!.slice(m[1]!.lastIndexOf(".") + 1) });
  return out;
}

function plain(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

/** Blocks of a description in order: paragraphs, list items and headings, with their inner HTML. */
function blocks(html: string): { tag: string; inner: string }[] {
  const out: { tag: string; inner: string }[] = [];
  for (const m of html.matchAll(/<(p|li|h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/gi)) out.push({ tag: m[1]!.toLowerCase(), inner: m[2]! });
  return out;
}

function roleOf(word: string): SpellRole | null {
  const w = word.toLowerCase();
  if (w.startsWith("cantrip")) return "cantrip";
  if (w === "initial" || w === "advanced" || w === "greater") return w;
  const m = ORDINAL.exec(w);
  if (m) {
    const rank = Number(m[1]);
    return rank >= 1 && rank <= 10 ? (rank as SpellRole) : null;
  }
  return null;
}

/** Split a block's inner HTML into role-headed groups of links: "initial: @UUID..., advanced: @UUID...". */
function groupsOf(inner: string): LabelledSpellList["groups"] {
  const groups: LabelledSpellList["groups"] = [];
  // Role words directly in front of a link run: "cantrips:", "1st:", "initial:", each optionally wrapped in <strong>.
  const re = /(?:<strong>\s*)?\b(cantrips?|initial|advanced|greater|\d+(?:st|nd|rd|th))\b\s*:?\s*(?:<\/strong>)?\s*(?=@UUID)/gi;
  let last = 0;
  let role: SpellRole | null = null;
  const push = (segment: string): void => {
    const spells = links(segment);
    if (spells.length > 0) groups.push({ role, spells });
  };
  for (const m of inner.matchAll(re)) {
    push(inner.slice(last, m.index));
    role = roleOf(m[1]!);
    last = m.index + m[0].length;
  }
  push(inner.slice(last));
  return groups;
}

/**
 * Bold-labelled spell lists: a block starting with `<strong>Label</strong>` whose remainder links
 * spells, and a `<strong>Label</strong>` heading paragraph followed by list items of links (a
 * wizard school's Curriculum).
 */
export function labelledSpellLists(html: string): LabelledSpellList[] {
  const out: LabelledSpellList[] = [];
  let heading: string | null = null;
  let pending: LabelledSpellList | null = null;
  const flush = (): void => {
    if (pending && pending.groups.length > 0) out.push(pending);
    pending = null;
  };
  for (const block of blocks(html)) {
    const labelled = /^\s*<strong>\s*([^<]*?)\s*(?::\s*)?<\/strong>\s*:?\s*([\s\S]*)$/.exec(block.inner);
    const linkOnly = block.tag === "p" && /^\s*(?:@UUID\[[^\]]+\](?:\{[^}]*\})?[\s,;]*)+$/.test(block.inner);
    if ((block.tag === "li" || linkOnly) && heading) {
      // A bullet or a bare link paragraph under a heading: its own role word (if any) applies to its links.
      pending ??= { label: heading, groups: [] };
      pending.groups.push(...groupsOf(block.inner));
      continue;
    }
    if (!labelled) {
      if (block.tag !== "li") {
        flush();
        heading = null;
      }
      continue;
    }
    const label = plain(labelled[1]!);
    const rest = labelled[2]!;
    if (plain(rest) === "" || !SPELL_LINK.test(rest)) {
      SPELL_LINK.lastIndex = 0;
      flush();
      heading = label;
      continue;
    }
    SPELL_LINK.lastIndex = 0;
    flush();
    heading = null;
    const groups = groupsOf(rest);
    if (groups.length > 0) out.push({ label, groups });
  }
  flush();
  return out;
}

/** The tradition a feature names under "Spell List" or "Tradition" (bloodlines, patrons, eidolons). */
export function spellListTradition(html: string): MagicTradition | null {
  const m = /<strong>\s*(?:Spell List|Tradition)\s*<\/strong>\s*([a-z]+)/i.exec(html);
  const word = m?.[1]?.toLowerCase();
  return word && (TRADITIONS as readonly string[]).includes(word) ? (word as MagicTradition) : null;
}

/** "Your spellcasting attribute is Charisma" / "key spellcasting attribute for wizard archetype spells is Intelligence". */
export function spellcastingAttribute(html: string): "str" | "dex" | "con" | "int" | "wis" | "cha" | null {
  const m = /spellcasting attribute (?:for [^.]*? )?is (Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma)/i.exec(plain(html));
  const names: Record<string, "str" | "dex" | "con" | "int" | "wis" | "cha"> = { strength: "str", dexterity: "dex", constitution: "con", intelligence: "int", wisdom: "wis", charisma: "cha" };
  return m ? (names[m[1]!.toLowerCase()] ?? null) : null;
}

/** Sentences of a description with their links intact, in order. */
function sentences(html: string): string[] {
  const text = html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ");
  // Split on sentence ends that are not inside a link label.
  return text.split(/(?<=[.!?])\s+(?=[A-Z@])/).map((s) => s.trim()).filter(Boolean);
}

const USES: Record<string, number> = { once: 1, twice: 2, three: 3, four: 4, five: 5 };

function usesIn(sentence: string): number | null | undefined {
  if (/\bat will\b/i.test(sentence)) return null;
  const m = /\b(once|twice|three times|four times|five times|(\d+) times) per day\b/i.exec(sentence);
  if (!m) return undefined;
  if (m[2]) return Number(m[2]);
  return USES[m[1]!.toLowerCase().split(" ")[0]!] ?? 1;
}

function traditionIn(sentence: string): MagicTradition | null {
  const m = /\b(arcane|divine|occult|primal)\b(?=[^.]*\binnate\b)/i.exec(sentence) ?? /\b(arcane|divine|occult|primal)\b/i.exec(sentence);
  return m ? (m[1]!.toLowerCase() as MagicTradition) : null;
}

/**
 * Innate-spell statements. A statement spans the sentence with "innate" and, when that sentence
 * has no link, the sentence before it ("Choose one cantrip from the primal spell list. You can
 * cast this spell as a primal innate spell at will.").
 */
export function innateStatements(html: string): InnateStatement[] {
  const out: InnateStatement[] = [];
  const list = sentences(html);
  for (let i = 0; i < list.length; i++) {
    const sentence = list[i]!;
    if (!/\binnate\b/i.test(sentence)) continue;
    const previous = i > 0 ? list[i - 1]! : "";
    const own = links(sentence);
    const choose = /\bchoose (?:one|a|an|two|three)\b/i.exec(previous) || /\bchoose (?:one|a|an|two|three)\b/i.exec(sentence) ? (previous + " " + sentence) : null;
    const chosen = own.length === 0 && /\bthe chosen (?:spell|cantrip)\b/i.test(sentence) && /\bchoose (?:either|one of|one)\b/i.test(previous) ? links(previous) : [];
    const spells = own.length > 0 ? own : chosen.length === 0 && /\bthis (?:spell|cantrip)\b/i.test(sentence) ? links(previous) : [];
    const ownUses = usesIn(sentence);
    const previousUses = usesIn(previous);
    const uses = ownUses !== undefined ? ownUses : previousUses !== undefined ? previousUses : 1;
    const rankMatch = ORDINAL.exec(sentence.replace(/heightened[^.]*/i, "")) ?? (own.length === 0 ? ORDINAL.exec(previous) : null);
    const rank = rankMatch && /rank/i.test(sentence + previous) ? Number(rankMatch[1]) : null;
    let choice: InnateStatement["choose"] = null;
    if (chosen.length > 1) choice = { cantrip: false, rank: rank && rank <= 10 ? rank : null, tradition: traditionIn(sentence), candidates: chosen };
    else if (spells.length === 0 && choose) {
      const m = /\bchoose (?:one|a|an|two|three) (cantrip|(\d+)(?:st|nd|rd|th)-rank spell|spell)s? (?:from|of|on) the (arcane|divine|occult|primal) (?:spell )?list/i.exec(choose);
      if (m) choice = { cantrip: m[1]!.toLowerCase() === "cantrip", rank: m[2] ? Number(m[2]) : m[1]!.toLowerCase() === "cantrip" ? 0 : null, tradition: m[3]!.toLowerCase() as MagicTradition, candidates: null };
    }
    if (spells.length === 0 && !choice) continue;
    out.push({ spells, tradition: traditionIn(sentence) ?? (choice?.tradition ?? traditionIn(previous)), uses, rank: rank && rank <= 10 ? rank : null, choose: choice });
  }
  return out;
}

/**
 * Sentences that give spells outright: "You gain X", "You learn the X composition spell", "your
 * familiar learns X", "Choose either X or Y", "You gain X, Y, or another 1st-rank monk qi spell".
 * Innate sentences are left to `innateStatements`; sentences that merely mention a spell ("as if
 * you had cast X", "instead of X") are skipped.
 */
export function grantSentences(html: string): GrantSentence[] {
  const out: GrantSentence[] = [];
  for (const sentence of sentences(html)) {
    const spells = links(sentence);
    if (spells.length === 0 || /\binnate\b/i.test(sentence)) continue;
    const text = sentence.replace(/@UUID\[[^\]]+\](?:\{[^}]*\})?/g, "X");
    const gives = /\b(?:you|your familiar) (?:gain|gains|learn|learns|know|knows)\b/i.test(text) || /\b(?:choose|select) (?:either|one|a|an)\b/i.test(text) || /\byou add\b[^.]*\brepertoire\b/i.test(text);
    const mentions = /\b(?:as if|instead of|in place of|rather than|replaces?|counts? as|like|such as|similar to|the effects? of|were cast|been cast|cast(?:ing)? on you)\b/i.test(text);
    if (!gives || mentions) continue;
    const another = /\bor another (?:(\d+)(?:st|nd|rd|th)-rank )?(?:([a-z]+) )?(?:[a-z]+ )?spell\b/i.exec(text);
    const choose = spells.length > 1 && (/\bor\b/i.test(text) || /\b(?:choose|select)\b/i.test(text));
    out.push({
      spells,
      choose: choose || another !== null,
      another: another ? { rank: another[1] ? Number(another[1]) : null, trait: another[2] ? another[2].toLowerCase() : null } : null,
      familiar: /\bfamiliar (?:learns|knows|gains)\b/i.test(text),
      repertoire: /\bto your ([a-z]+) spell repertoire\b/i.exec(text)?.[1]?.toLowerCase() ?? null,
      text: plain(sentence),
    });
  }
  return out;
}

/** "Your alternate form is a fox, which has the statistics of 1st-rank Pest Form": a form the Entry gives, stated as a spell at a rank. */
export interface FormStatement {
  spells: SpellLink[];
  rank: number | null;
  text: string;
}

/** Forms an Entry gives with a spell's statistics (a kitsune heritage's fox form). */
export function formStatements(html: string): FormStatement[] {
  const out: FormStatement[] = [];
  for (const sentence of sentences(html)) {
    const m = /\bhas the statistics of (?:a |an )?(?:(\d+)(?:st|nd|rd|th)-rank )?(@UUID\[Compendium\.pf2e\.spells-srd\.Item\.[^\]]+\](?:\{[^}]*\})?)/i.exec(sentence);
    if (!m) continue;
    const spells = links(m[2]!);
    if (spells.length === 0) continue;
    out.push({ spells, rank: m[1] ? Number(m[1]) : null, text: plain(sentence) });
  }
  return out;
}

/** "You gain a devotion spell of your choice listed in the devotion spells class feature": the feature whose list the pick comes from. */
export interface FeatureReference {
  /** What is gained ("devotion spell"). */
  what: string;
  /** The class feature's name as written, lower case. */
  feature: string;
  text: string;
}

export function featureReferences(html: string): FeatureReference[] {
  const out: FeatureReference[] = [];
  for (const sentence of sentences(html)) {
    const m = /\byou gain (?:a|an|one) ([a-z ]+? spell) of your choice listed in the ([a-z' ]+?) class feature\b/i.exec(sentence);
    if (m) out.push({ what: m[1]!.toLowerCase(), feature: m[2]!.toLowerCase(), text: plain(sentence) });
  }
  return out;
}

/**
 * Archetype spellcasting a feat describes in prose rather than by Foundry's dedication tag ("You
 * also gain the basic spellcasting benefits", Red Mantis Magic): the tier, the tradition, whether
 * spells are prepared or a repertoire, the cantrips it gives, and the spell school it opens.
 */
export interface SpellcastingBenefits {
  tier: "basic" | "expert" | "master";
  tradition: MagicTradition | null;
  casting: "prepared" | "spontaneous" | null;
  /** Cantrips the feat gives (the printed rule's two), when it names them; null otherwise. */
  cantrips: number | null;
  /** A school or curriculum Entry the feat gives access to (a classfeatures link). */
  schools: string[];
  text: string;
}

const COUNTS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 };

export function spellcastingBenefits(html: string): SpellcastingBenefits | null {
  const text = plain(html);
  const tier = /\bgain the (basic|expert|master) spellcasting benefits\b/i.exec(text)?.[1]?.toLowerCase() as SpellcastingBenefits["tier"] | undefined;
  if (!tier) return null;
  const traditionMatch = /\b(?:from|on) the (arcane|divine|occult|primal) spell list\b/i.exec(text) ?? /\b(?:they|these|the spells) are (arcane|divine|occult|primal) \b/i.exec(text) ?? /\bare (arcane|divine|occult|primal) [a-z ]*spells\b/i.exec(text);
  const tradition = traditionMatch ? (traditionMatch[1]!.toLowerCase() as MagicTradition) : null;
  const casting: SpellcastingBenefits["casting"] = /\bprepare\b/i.test(text) ? "prepared" : /\brepertoire\b|\bspontaneous(?:ly)?\b/i.test(text) ? "spontaneous" : null;
  const cantripMatch = /\b(one|two|three|four|five|six|\d+) (?:common )?cantrips?\b/i.exec(text);
  const cantrips = cantripMatch ? (COUNTS[cantripMatch[1]!.toLowerCase()] ?? Number(cantripMatch[1])) : null;
  const schools: string[] = [];
  for (const m of html.matchAll(/@UUID\[(Compendium\.pf2e\.classfeatures\.Item\.[^\]]+)\]/g)) schools.push(m[1]!);
  return { tier, tradition, casting, cantrips: Number.isFinite(cantrips) ? cantrips : null, schools, text };
}

/**
 * A feat that gives a spontaneous caster a spellbook to draw on (Arcane Evolution: "You can use
 * the Learn a Spell activity to add more arcane spells to the list you choose from"; the older
 * "you gain a spellbook"). The tradition when the sentence names one.
 */
export function spellbookStatement(html: string): { tradition: MagicTradition | null; text: string } | null {
  for (const sentence of sentences(html)) {
    const m = /\bLearn a Spell\b[^.]*?\badd (?:more )?(?:(arcane|divine|occult|primal) )?spells to (?:the list you choose from|your spellbook)\b/i.exec(sentence) ?? /\byou gain a spellbook\b(?: [^.]*?(arcane|divine|occult|primal))?/i.exec(sentence);
    if (m) return { tradition: m[1] ? (m[1].toLowerCase() as MagicTradition) : null, text: plain(sentence) };
  }
  return null;
}

/** The ranked spell list a staff carries: every ranked group of every labelled list, in order (its "Effect" bullets). */
export function itemSpellList(html: string): { role: SpellRole; spells: SpellLink[] }[] {
  const out: { role: SpellRole; spells: SpellLink[] }[] = [];
  for (const list of labelledSpellLists(html)) {
    for (const group of list.groups) if (group.role === "cantrip" || typeof group.role === "number") out.push({ role: group.role, spells: group.spells });
  }
  return out;
}

/** Every spell linked anywhere in a description, for the loose checks. */
export function allSpellLinks(html: string): SpellLink[] {
  return links(html);
}
